import { useState, useEffect, useMemo } from "react";
import { PlusCircle, RefreshCw, Search, ChevronDown, ChevronRight, FolderOpen, Copy, EyeOff, Eye, GitBranch } from "lucide-react";
import "./PromptList.css";
import { apiService, type Prompt } from "../services/api.service";
import { OrgCombobox } from "./users-config/OrgCombobox";
import { PROMPT_APPLICATIONS } from "../config/api.config";
import { PromptEditor } from "./PromptEditor";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const APPLICATION_OPTIONS = [
  { value: "all", label: "All Applications" },
  ...PROMPT_APPLICATIONS,
];

const CATEGORY_OPTIONS: Array<{ value: string; label: string }> = [
  { value: "all", label: "All Categories" },
  { value: "system", label: "system" },
  { value: "user", label: "user" },
  { value: "assistant", label: "assistant" },
  { value: "tool", label: "tool" },
];

export const PromptList = () => {
  const [prompts, setPrompts] = useState<Prompt[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedApp, setSelectedApp] = useState("all");
  const [selectedPrompt, setSelectedPrompt] = useState<Prompt | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [orgFilter, setOrgFilter] = useState("all");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set());
  const [duplicateTarget, setDuplicateTarget] = useState<{ prompt: Prompt; targetOrgId: string } | null>(null);
  const [orgsFromApi, setOrgsFromApi] = useState<{ id: string; name: string }[]>([]);
  const orgIdToName = useMemo(() => {
    const map: Record<string, string> = {};
    for (const org of orgsFromApi) map[org.id] = org.name;
    return map;
  }, [orgsFromApi]);
  const [isDuplicating, setIsDuplicating] = useState(false);
  const [showHidden, setShowHidden] = useState(false);
  const [subtab, setSubtab] = useState<"prompts" | "canned-messages">("prompts");
  const [collapsedTrees, setCollapsedTrees] = useState<Set<string>>(new Set());

  const orgOptions = useMemo(() => {
    return [
      { value: "all", label: "All Orgs" },
      { value: "core", label: "Core" },
      ...orgsFromApi.map((org) => ({ value: org.id, label: org.name })),
    ];
  }, [orgsFromApi]);

  const filteredPrompts = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();

    return prompts.filter((prompt) => {
      const isCannedMessage = prompt.tags.includes("canned-message");
      const matchesSubtab = subtab === "canned-messages" ? isCannedMessage : !isCannedMessage;
      const matchesSearch =
        !q ||
        prompt.name.toLowerCase().includes(q) ||
        prompt.prompt_id.toLowerCase().includes(q) ||
        prompt.description?.toLowerCase().includes(q) ||
        prompt.tags.some((tag) => tag.toLowerCase().includes(q));
      const promptOrgId = prompt.org?.trim();
      const matchesOrg =
        orgFilter === "all" ||
        (orgFilter === "core" ? !promptOrgId : promptOrgId === orgFilter);
      const matchesCategory =
        categoryFilter === "all" || prompt.category === categoryFilter;

      return matchesSubtab && matchesSearch && matchesOrg && matchesCategory;
    });
  }, [prompts, searchQuery, orgFilter, categoryFilter, subtab]);

  useEffect(() => {
    loadPrompts();
  }, [selectedApp, showHidden]);

  useEffect(() => {
    apiService.getOrganizations().then(setOrgsFromApi).catch(() => {});
  }, []);

  const loadPrompts = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await apiService.getPrompts(
        selectedApp === "all" ? undefined : selectedApp,
        undefined,
        undefined,
        showHidden,
      );
      setPrompts(data);
    } catch {
      setError("Failed to load prompts");
    } finally {
      setIsLoading(false);
    }
  };

  const groupedPrompts = useMemo(() => {
    const groups: Record<string, Prompt[]> = {};
    for (const prompt of filteredPrompts) {
      const key = prompt.org?.trim() || "core";
      if (!groups[key]) groups[key] = [];
      groups[key].push(prompt);
    }
    const sorted = Object.entries(groups).sort(([a], [b]) => {
      if (a === "core") return -1;
      if (b === "core") return 1;
      return a.localeCompare(b);
    });
    return sorted;
  }, [filteredPrompts]);

  // Build reference graph: prompt_id -> child prompt_ids
  // Checks ph.prompt_id (explicit), ph.description (legacy overloaded field), and ph.name matching a known prompt_id
  const promptHierarchy = useMemo(() => {
    const allPromptIds = new Set(filteredPrompts.map((p) => p.prompt_id));
    const childrenMap = new Map<string, string[]>();
    const referencedIds = new Set<string>();
    const parentsMap = new Map<string, string[]>();

    for (const prompt of filteredPrompts) {
      const children: string[] = [];
      for (const ph of prompt.placeholders ?? []) {
        // Resolve child prompt_id: explicit field > description (legacy) > name matching a prompt_id
        const childId =
          ph.prompt_id ||
          (ph.description && ph.description !== "__runtime__" && allPromptIds.has(ph.description) ? ph.description : null) ||
          (allPromptIds.has(ph.name) && ph.name !== prompt.prompt_id ? ph.name : null);

        if (childId) {
          if (!children.includes(childId)) children.push(childId);
          referencedIds.add(childId);
          const parents = parentsMap.get(childId) ?? [];
          if (!parents.includes(prompt.prompt_id)) {
            parents.push(prompt.prompt_id);
          }
          parentsMap.set(childId, parents);
        }
      }
      if (children.length > 0) {
        childrenMap.set(prompt.prompt_id, children);
      }
    }

    return { childrenMap, referencedIds, parentsMap };
  }, [filteredPrompts]);

  // allOrgs is now sourced from the organizations API (same as TV/user flows)
  const allOrgs = orgsFromApi;

  const hasActiveFilters =
    Boolean(searchQuery.trim()) || orgFilter !== "all" || categoryFilter !== "all";

  const getCategoryVariant = (category: Prompt["category"]) => {
    if (category === "system") return "default";
    if (category === "user") return "secondary";
    if (category === "assistant") return "outline";
    return "secondary";
  };

  const formatDate = (dateStr: string) => {
    if (!dateStr) return "N/A";
    const d = new Date(dateStr);
    if (Number.isNaN(d.getTime())) return "N/A";
    return d.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const toggleGroup = (key: string) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const toggleTree = (promptId: string) => {
    setCollapsedTrees((prev) => {
      const next = new Set(prev);
      if (next.has(promptId)) next.delete(promptId);
      else next.add(promptId);
      return next;
    });
  };

  const handleToggleHidden = async (prompt: Prompt, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await apiService.updatePrompt(prompt.application_id, prompt.prompt_id, {
        hidden: !prompt.hidden,
      });
      loadPrompts();
    } catch {
      setError("Failed to update prompt visibility");
    }
  };

  const handleDuplicateToOrg = async () => {
    if (!duplicateTarget) return;
    setIsDuplicating(true);
    try {
      const { prompt, targetOrgId } = duplicateTarget;
      const targetOrgName = orgIdToName[targetOrgId] || targetOrgId;
      const versions = await apiService.getPromptVersions(prompt.application_id, prompt.prompt_id);
      const activeVersion = versions.find((v) => v.status === "active") || versions[0];
      await apiService.createPrompt({
        application_id: prompt.application_id,
        prompt_id: `${prompt.prompt_id}-${targetOrgName.toLowerCase().replace(/\s+/g, "-")}`,
        name: `${prompt.name} (${targetOrgName})`,
        description: prompt.description,
        category: prompt.category,
        org: targetOrgId,
        tags: prompt.tags,
        placeholders: prompt.placeholders,
        content: activeVersion?.content || "",
      });
      setDuplicateTarget(null);
      loadPrompts();
    } catch {
      setError("Failed to duplicate prompt");
    } finally {
      setIsDuplicating(false);
    }
  };

  if (selectedPrompt) {
    return (
      <PromptEditor
        prompt={selectedPrompt}
        onBack={() => {
          setSelectedPrompt(null);
          loadPrompts();
        }}
      />
    );
  }

  if (showCreateModal) {
    return (
      <PromptEditor
        prompt={null}
        onBack={() => {
          setShowCreateModal(false);
          loadPrompts();
        }}
        initialTags={subtab === "canned-messages" ? "canned-message" : undefined}
      />
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-col gap-4 overflow-y-auto pr-1">
      <div className="flex gap-2 border-b">
        <button
          type="button"
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${subtab === "prompts" ? "border-primary text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"}`}
          onClick={() => setSubtab("prompts")}
        >
          Prompts
        </button>
        <button
          type="button"
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${subtab === "canned-messages" ? "border-primary text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"}`}
          onClick={() => setSubtab("canned-messages")}
        >
          Canned Messages
        </button>
      </div>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <Select value={selectedApp} onValueChange={setSelectedApp}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Filter by application" />
            </SelectTrigger>
            <SelectContent>
              {APPLICATION_OPTIONS.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={orgFilter} onValueChange={setOrgFilter}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Filter by org" />
            </SelectTrigger>
            <SelectContent>
              {orgOptions.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={categoryFilter} onValueChange={setCategoryFilter}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Filter by category" />
            </SelectTrigger>
            <SelectContent>
              {CATEGORY_OPTIONS.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="relative">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search prompts..."
              className="w-[220px] pl-8"
            />
          </div>
          <Button
            variant={showHidden ? "default" : "outline"}
            size="sm"
            onClick={() => setShowHidden((v) => !v)}
            title={showHidden ? "Showing hidden prompts" : "Hidden prompts are filtered out"}
          >
            {showHidden ? <Eye className="mr-1 h-4 w-4" /> : <EyeOff className="mr-1 h-4 w-4" />}
            {showHidden ? "Showing hidden" : "Show hidden"}
          </Button>
        </div>

        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" onClick={loadPrompts}>
            <RefreshCw className="h-4 w-4" />
          </Button>
          <Button onClick={() => setShowCreateModal(true)}>
            <PlusCircle className="h-4 w-4" />
            {subtab === "canned-messages" ? "New Canned Message" : "New Prompt"}
          </Button>
        </div>
      </div>

      {isLoading ? (
        <Card className="p-8 text-center text-sm text-muted-foreground">Loading prompts...</Card>
      ) : error ? (
        <Card className="space-y-3 p-8 text-center">
          <p className="text-sm text-destructive">{error}</p>
          <Button onClick={loadPrompts}>Retry</Button>
        </Card>
      ) : filteredPrompts.length === 0 ? (
        <Card className="space-y-3 p-8 text-center">
          <h3 className="font-semibold">{hasActiveFilters ? "No matching prompts" : "No prompts found"}</h3>
          <p className="text-sm text-muted-foreground">
            {hasActiveFilters
              ? "No prompts match the current search and filters."
              : "Create your first prompt to get started."}
          </p>
          {!hasActiveFilters && <Button onClick={() => setShowCreateModal(true)}>Create Prompt</Button>}
        </Card>
      ) : (
        <div className="space-y-4">
          {groupedPrompts.map(([orgKey, orgPrompts]) => (
            <div key={orgKey} className="rounded-lg border bg-card">
              <button
                type="button"
                className="flex w-full items-center gap-2 px-4 py-3 text-left hover:bg-muted/50"
                onClick={() => toggleGroup(orgKey)}
              >
                {collapsedGroups.has(orgKey) ? (
                  <ChevronRight className="h-4 w-4 shrink-0" />
                ) : (
                  <ChevronDown className="h-4 w-4 shrink-0" />
                )}
                <FolderOpen className="h-4 w-4 shrink-0 text-muted-foreground" />
                <span className="font-medium capitalize">{orgKey === "core" ? "core" : (orgIdToName[orgKey] || orgKey)}</span>
                <Badge variant="secondary" className="ml-1 text-xs">
                  {orgPrompts.length}
                </Badge>
              </button>

              {!collapsedGroups.has(orgKey) && (() => {
                const { childrenMap, referencedIds, parentsMap } = promptHierarchy;
                const orgPromptIds = new Set(orgPrompts.map((p) => p.prompt_id));
                const renderedIds = new Set<string>();

                // Partition into standalone, tree roots, and orphan subprompts
                const standalonePrompts: Prompt[] = [];
                const treeRoots: Prompt[] = [];

                for (const p of orgPrompts) {
                  const isReferenced = referencedIds.has(p.prompt_id);
                  const hasChildren = (childrenMap.get(p.prompt_id) ?? []).some((id) => orgPromptIds.has(id));
                  if (!isReferenced && !hasChildren) {
                    standalonePrompts.push(p);
                  } else if (!isReferenced) {
                    treeRoots.push(p);
                  }
                }

                const renderCardActions = (prompt: Prompt) => (
                  <div className="flex items-center gap-1">
                    <Badge variant="outline">{prompt.application_id}</Badge>
                    <button
                      type="button"
                      title={prompt.hidden ? "Unhide prompt" : "Hide prompt"}
                      className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
                      onClick={(e) => handleToggleHidden(prompt, e)}
                    >
                      {prompt.hidden ? <Eye className="h-3.5 w-3.5" /> : <EyeOff className="h-3.5 w-3.5" />}
                    </button>
                    <button
                      type="button"
                      title="Duplicate to another org"
                      className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
                      onClick={(e) => {
                        e.stopPropagation();
                        setDuplicateTarget({ prompt, targetOrgId: "" });
                      }}
                    >
                      <Copy className="h-3.5 w-3.5" />
                    </button>
                  </div>
                );

                const renderStandaloneCard = (prompt: Prompt) => {
                  renderedIds.add(prompt.prompt_id);
                  return (
                    <Card
                      key={`${prompt.application_id}-${prompt.prompt_id}`}
                      className={`cursor-pointer space-y-3 p-4 transition hover:border-primary/40 hover:shadow-sm ${prompt.hidden ? "opacity-50" : ""}`}
                      onClick={() => setSelectedPrompt(prompt)}
                    >
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <div className="flex flex-wrap items-center gap-2">
                          <Badge variant={getCategoryVariant(prompt.category)}>{prompt.category}</Badge>
                        </div>
                        {renderCardActions(prompt)}
                      </div>
                      <div>
                        <h3 className="font-semibold">{prompt.name}</h3>
                        <p className="line-clamp-2 text-sm text-muted-foreground">{prompt.description}</p>
                      </div>
                      <div className="flex items-center justify-between text-xs text-muted-foreground">
                        <span>Active v{prompt.active_version}</span>
                        <span>Latest v{prompt.latest_version}</span>
                      </div>
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-xs text-muted-foreground">Updated {formatDate(prompt.updated_at)}</span>
                        {prompt.tags.length > 0 && (
                          <div className="flex gap-1">
                            {prompt.tags.slice(0, 2).map((tag) => (
                              <Badge key={tag} variant="secondary" className="text-[10px]">{tag}</Badge>
                            ))}
                          </div>
                        )}
                      </div>
                    </Card>
                  );
                };

                const renderSubpromptCard = (prompt: Prompt, depth: number, parentId: string): React.ReactNode => {
                  renderedIds.add(prompt.prompt_id);
                  const childIds = (childrenMap.get(prompt.prompt_id) ?? []).filter((id) => orgPromptIds.has(id) && !renderedIds.has(id));
                  const childPrompts = childIds.map((id) => orgPrompts.find((p) => p.prompt_id === id)).filter(Boolean) as Prompt[];
                  const allParents = parentsMap.get(prompt.prompt_id) ?? [];
                  const otherParents = allParents.filter((pid) => pid !== parentId);
                  const depthClass = depth <= 3 ? `tree-depth-${depth}` : "tree-depth-3";

                  return (
                    <div key={`${prompt.application_id}-${prompt.prompt_id}`} className={`tree-node ${depthClass}`}>
                      <Card
                        className={`subprompt-card cursor-pointer space-y-2 p-3 transition hover:border-primary/40 hover:shadow-sm ${prompt.hidden ? "opacity-50" : ""}`}
                        onClick={() => setSelectedPrompt(prompt)}
                      >
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="subprompt-badge">Subprompt</span>
                            <Badge variant={getCategoryVariant(prompt.category)} className="text-[10px]">{prompt.category}</Badge>
                          </div>
                          {renderCardActions(prompt)}
                        </div>
                        <div>
                          <h4 className="text-sm font-semibold">{prompt.name}</h4>
                          <p className="line-clamp-1 text-xs text-muted-foreground">{prompt.description}</p>
                          {otherParents.length > 0 && (
                            <p className="text-xs text-muted-foreground mt-0.5 italic">Also referenced by: {otherParents.join(", ")}</p>
                          )}
                        </div>
                        <div className="flex items-center justify-between text-xs text-muted-foreground">
                          <span>v{prompt.active_version}</span>
                          <span>Updated {formatDate(prompt.updated_at)}</span>
                        </div>
                      </Card>
                      {childPrompts.length > 0 && (
                        <div className="tree-children">
                          {childPrompts.map((child) => renderSubpromptCard(child, depth + 1, prompt.prompt_id))}
                        </div>
                      )}
                    </div>
                  );
                };

                const renderHierarchyGroup = (rootPrompt: Prompt) => {
                  renderedIds.add(rootPrompt.prompt_id);
                  const childIds = (childrenMap.get(rootPrompt.prompt_id) ?? []).filter((id) => orgPromptIds.has(id) && !renderedIds.has(id));
                  const childPrompts = childIds.map((id) => orgPrompts.find((p) => p.prompt_id === id)).filter(Boolean) as Prompt[];
                  const isCollapsed = collapsedTrees.has(rootPrompt.prompt_id);

                  return (
                    <div key={`tree-${rootPrompt.prompt_id}`} className="hierarchy-group">
                      <Card
                        className={`cursor-pointer space-y-3 p-4 transition hover:border-primary/40 hover:shadow-sm border-0 rounded-b-none shadow-none ${rootPrompt.hidden ? "opacity-50" : ""}`}
                        onClick={() => setSelectedPrompt(rootPrompt)}
                      >
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <div className="flex flex-wrap items-center gap-2">
                            <Badge variant={getCategoryVariant(rootPrompt.category)}>{rootPrompt.category}</Badge>
                            <span className="children-count">
                              <GitBranch className="h-3 w-3" />
                              {childIds.length} subprompt{childIds.length !== 1 ? "s" : ""}
                            </span>
                          </div>
                          {renderCardActions(rootPrompt)}
                        </div>
                        <div>
                          <h3 className="font-semibold">{rootPrompt.name}</h3>
                          <p className="line-clamp-2 text-sm text-muted-foreground">{rootPrompt.description}</p>
                        </div>
                        <div className="flex items-center justify-between text-xs text-muted-foreground">
                          <span>Active v{rootPrompt.active_version}</span>
                          <span>Latest v{rootPrompt.latest_version}</span>
                        </div>
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-xs text-muted-foreground">Updated {formatDate(rootPrompt.updated_at)}</span>
                          {rootPrompt.tags.length > 0 && (
                            <div className="flex gap-1">
                              {rootPrompt.tags.slice(0, 2).map((tag) => (
                                <Badge key={tag} variant="secondary" className="text-[10px]">{tag}</Badge>
                              ))}
                            </div>
                          )}
                        </div>
                      </Card>
                      <button
                        type="button"
                        className="flex w-full items-center gap-2 px-4 py-1.5 text-xs text-muted-foreground hierarchy-toggle"
                        onClick={(e) => { e.stopPropagation(); toggleTree(rootPrompt.prompt_id); }}
                      >
                        {isCollapsed ? <ChevronRight className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                        {isCollapsed ? "Show" : "Hide"} {childIds.length} subprompt{childIds.length !== 1 ? "s" : ""}
                      </button>
                      {!isCollapsed && (
                        <div className="tree-children px-3 py-2">
                          {childPrompts.map((child) => renderSubpromptCard(child, 1, rootPrompt.prompt_id))}
                        </div>
                      )}
                    </div>
                  );
                };

                // Render orphan subprompts (referenced but parent not in this org group)
                const renderOrphanCard = (prompt: Prompt) => {
                  renderedIds.add(prompt.prompt_id);
                  const allParents = parentsMap.get(prompt.prompt_id) ?? [];
                  return (
                    <Card
                      key={`${prompt.application_id}-${prompt.prompt_id}`}
                      className={`subprompt-card cursor-pointer space-y-3 p-4 transition hover:border-primary/40 hover:shadow-sm ${prompt.hidden ? "opacity-50" : ""}`}
                      onClick={() => setSelectedPrompt(prompt)}
                    >
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="subprompt-badge">Subprompt</span>
                          <Badge variant={getCategoryVariant(prompt.category)}>{prompt.category}</Badge>
                        </div>
                        {renderCardActions(prompt)}
                      </div>
                      <div>
                        <h3 className="font-semibold">{prompt.name}</h3>
                        <p className="line-clamp-2 text-sm text-muted-foreground">{prompt.description}</p>
                        {allParents.length > 0 && (
                          <p className="text-xs text-muted-foreground mt-1 italic">Referenced by: {allParents.join(", ")}</p>
                        )}
                      </div>
                      <div className="flex items-center justify-between text-xs text-muted-foreground">
                        <span>Active v{prompt.active_version}</span>
                        <span>Latest v{prompt.latest_version}</span>
                      </div>
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-xs text-muted-foreground">Updated {formatDate(prompt.updated_at)}</span>
                        {prompt.tags.length > 0 && (
                          <div className="flex gap-1">
                            {prompt.tags.slice(0, 2).map((tag) => (
                              <Badge key={tag} variant="secondary" className="text-[10px]">{tag}</Badge>
                            ))}
                          </div>
                        )}
                      </div>
                    </Card>
                  );
                };

                const hierarchyGroupElements = treeRoots.map((root) => renderHierarchyGroup(root));
                // Mark standalone IDs before computing orphans so they aren't double-rendered
                for (const p of standalonePrompts) renderedIds.add(p.prompt_id);
                const orphans = orgPrompts.filter((p) => !renderedIds.has(p.prompt_id));

                return (
                  <div className="space-y-3 px-4 pb-4">
                    {standalonePrompts.length > 0 && (
                      <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
                        {standalonePrompts.map((p) => renderStandaloneCard(p))}
                      </div>
                    )}
                    {hierarchyGroupElements.length > 0 && (
                      <div className="space-y-3">
                        {hierarchyGroupElements}
                      </div>
                    )}
                    {orphans.length > 0 && (
                      <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
                        {orphans.map((p) => renderOrphanCard(p))}
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>
          ))}
        </div>
      )}

      {/* Duplicate to Org Dialog */}
      {duplicateTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <Card className="w-[400px] space-y-4 p-6">
            <h3 className="text-lg font-semibold">Duplicate to Org</h3>
            <p className="text-sm text-muted-foreground">
              Duplicate <strong>{duplicateTarget.prompt.name}</strong> to another organization.
            </p>
            <div className="space-y-2">
              <label className="text-sm font-medium">Target Organization</label>
              <OrgCombobox
                value={duplicateTarget.targetOrgId || undefined}
                onChange={(id) => setDuplicateTarget((prev) => prev ? { ...prev, targetOrgId: id ?? "" } : null)}
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setDuplicateTarget(null)}>Cancel</Button>
              <Button
                disabled={!duplicateTarget.targetOrgId || isDuplicating}
                onClick={handleDuplicateToOrg}
              >
                {isDuplicating ? "Duplicating..." : "Duplicate"}
              </Button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};
